import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  functionalUpdate,
  OnChangeFn,
  SortingState,
} from "@tanstack/react-table";
import { useLocation } from "@tanstack/react-router";
import { useSearchParams } from "~/hooks/use-search-params";

const DEFAULT_SORT_PARAM = "sort";
const DEFAULT_SORT_DIRECTION_PARAM = "sortDir";

interface UseUrlSortingOptions {
  validColumnIds: string[];
  sortParam?: string;
  sortDirectionParam?: string;
}

const isSortDirection = (value: string | null): value is "asc" | "desc" =>
  value === "asc" || value === "desc";

const urlSorting = (
  params: URLSearchParams,
  validColumnIdsSet: Set<string>,
  sortParam: string,
  sortDirectionParam: string,
): SortingState | null => {
  const sortColumn = params.get(sortParam);
  const sortDirection = params.get(sortDirectionParam);

  if (
    sortColumn !== null &&
    validColumnIdsSet.has(sortColumn) &&
    isSortDirection(sortDirection)
  ) {
    return [{ id: sortColumn, desc: sortDirection === "desc" }];
  }

  return null;
};

export const useUrlSorting = ({
  validColumnIds,
  sortParam = DEFAULT_SORT_PARAM,
  sortDirectionParam = DEFAULT_SORT_DIRECTION_PARAM,
}: UseUrlSortingOptions) => {
  const { state } = useLocation();
  const [params, setSearchParams] = useSearchParams();
  const paramsString = params.toString();
  const currentParams = useMemo(
    () => new URLSearchParams(paramsString),
    [paramsString],
  );

  const validColumnIdsSet = useMemo(
    () => new Set(validColumnIds),
    [validColumnIds],
  );

  const hasSortParams =
    currentParams.has(sortParam) || currentParams.has(sortDirectionParam);
  const parsedUrlSorting = useMemo(
    () =>
      urlSorting(
        currentParams,
        validColumnIdsSet,
        sortParam,
        sortDirectionParam,
      ),
    [currentParams, sortDirectionParam, sortParam, validColumnIdsSet],
  );
  const [localSorting, setLocalSorting] = useState<SortingState>(
    () => parsedUrlSorting ?? [],
  );
  const sortingRef = useRef(localSorting);

  const updateSorting = useCallback((nextSorting: SortingState) => {
    sortingRef.current = nextSorting;
    setLocalSorting(nextSorting);
  }, []);

  const setSorting = useCallback<OnChangeFn<SortingState>>(
    (updaterOrValue) => {
      const nextSorting = functionalUpdate(updaterOrValue, sortingRef.current);
      const [nextSort] = nextSorting;
      const nextParams = new URLSearchParams(paramsString);

      if (nextSort && validColumnIdsSet.has(nextSort.id)) {
        updateSorting([nextSort]);
        nextParams.set(sortParam, nextSort.id);
        nextParams.set(sortDirectionParam, nextSort.desc ? "desc" : "asc");
      } else {
        updateSorting([]);
        nextParams.delete(sortParam);
        nextParams.delete(sortDirectionParam);
      }

      setSearchParams(nextParams, { replace: true, state });
    },
    [
      paramsString,
      setSearchParams,
      state,
      sortDirectionParam,
      sortParam,
      updateSorting,
      validColumnIdsSet,
    ],
  );

  useEffect(() => {
    if (parsedUrlSorting) {
      // keeps local sorting in sync with the URL query params (the external source of truth) while the same effect also normalizes invalid params via navigate
      updateSorting(parsedUrlSorting);
      return;
    }

    if (!hasSortParams) {
      updateSorting([]);
      return;
    }

    const nextParams = new URLSearchParams(paramsString);
    nextParams.delete(sortParam);
    nextParams.delete(sortDirectionParam);
    updateSorting([]);

    if (nextParams.toString() === paramsString) {
      return;
    }

    setSearchParams(nextParams, { replace: true, state });
  }, [
    hasSortParams,
    paramsString,
    parsedUrlSorting,
    setSearchParams,
    state,
    sortDirectionParam,
    sortParam,
    updateSorting,
  ]);

  return {
    sorting: localSorting,
    setSorting,
  };
};
